import { useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowLeftRight,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Compass,
  Eye,
  Lock,
  LogOut,
  Monitor,
  Moon,
  Sun,
  Undo2,
  User,
  Users,
  Workflow,
} from "lucide-react";
import {
  RavenAppearanceMenu,
  RavenIcon,
  useRavenAppearance,
} from "@ravenappdev/raven-ui";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, TextField } from "../ds";
import { startFullTour } from "../tour";
import { useAuth } from "../context/AuthContext";
import { loadAuth } from "../context/authStorage";
import { openAuthHandoff } from "../utils/authHandoff";
import { useCurrentUserProfile } from "../hooks/useUserProfile";
import { isEmployeeOnly } from "../hooks/useIsAdmin";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import {
  getPresetUsers,
  isProdEnvironment,
  type PresetUser,
} from "../constants/presetUsers";

interface NavRowProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** Right-aligned content — the current value of a sub-menu row. */
  trailing?: ReactNode;
  "data-tour"?: string;
}

// Initials = first letter of the first 2 space-separated tokens.
// "Manikandan" → "M", "rishi admin" → "RA". Same logic as Profile.
function initialsOf(name: string | null | undefined): string {
  const tokens = (name || "?").split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
  return (
    tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)
  ).toUpperCase();
}

function NavRow({
  icon,
  label,
  onClick,
  danger,
  trailing,
  "data-tour": dataTour,
}: NavRowProps) {
  return (
    <ButtonBase
      onClick={onClick}
      data-tour={dataTour}
      sx={(theme) => ({
        width: "100%",
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        justifyContent: "flex-start",
        color: danger ? "error.main" : "text.primary",
        "&:hover": {
          bgcolor: danger
            ? alpha(theme.palette.error.main, 0.06)
            : alpha(theme.palette.text.primary, 0.05),
        },
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "inline-flex",
            color: danger ? "error.main" : "text.secondary",
          }}
        >
          {icon}
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 500, flex: 1, textAlign: "left" }}>
          {label}
        </Typography>
        {trailing}
      </Stack>
    </ButtonBase>
  );
}

// Mode preference → the glyph and the caption on the Appearance row. "system"
// shows the monitor rather than the mode it currently resolves to, because the
// row reports the *preference*, not the paint.
const APPEARANCE_LABELS = {
  light: { icon: Sun, caption: "Light" },
  dark: { icon: Moon, caption: "Dark" },
  system: { icon: Monitor, caption: "Match system" },
} as const;

export default function SwitchUserMenu() {
  const {
    auth,
    logout,
    switchUser,
    resetToOriginalUser,
    isImpersonating,
    originalAuth,
    userName,
    userRole,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [manualId, setManualId] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Appearance (colour mode + font) is the package provider's state; this row
  // is only a trigger for the package's own switcher, so the two never drift.
  const { mode } = useRavenAppearance();
  const appearanceRowRef = useRef<HTMLDivElement | null>(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const appearance = APPEARANCE_LABELS[mode];

  const presetUsers = useMemo<PresetUser[]>(
    () => getPresetUsers(auth?.user.account_id, isProdEnvironment()),
    [auth?.user.account_id],
  );

  const doSwitch = async (userId: string) => {
    const clean = userId.trim();
    if (!clean) return;
    setError(null);
    setPendingId(clean);
    try {
      await switchUser(clean);
    } catch (err) {
      setPendingId(null);
      let message = "Could not switch user.";
      if (axios.isAxiosError(err)) {
        message =
          (err.response?.data as { detail?: string } | undefined)?.detail ??
          err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    }
  };

  const handleReset = async () => {
    setError(null);
    setPendingId("__original__");
    try {
      await resetToOriginalUser();
    } catch {
      setPendingId(null);
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate("/login");
  };

  const { data: profile } = useCurrentUserProfile();

  // The menu has to finish closing before the tour starts, otherwise
  // driver.js's overlay captures the still-open popover and highlights it
  // instead of the page behind. The tour then navigates itself across
  // pages and returns the user here when it ends.
  const handleStartTour = () => {
    handleClose();
    setTimeout(() => {
      void startFullTour({
        navigate,
        currentPath: location.pathname,
        includeOverview: !isEmployeeOnly(profile?.roles),
      }).then((started) => {
        if (!started) {
          enqueueSnackbar(
            "Could not start the product tour — the Suggestions page is not available for your account.",
            { variant: "info" },
          );
        }
      });
    }, 250);
  };

  const adminFrontendUrl = process.env.REACT_APP_ADMIN_FRONTEND_URL;
  const { data: canManageUsers = false } = useIsAuthorized({
    action: "manage_users",
  });
  const showManageUsers = canManageUsers && Boolean(adminFrontendUrl);

  const handleManageUsers = () => {
    if (!auth || !adminFrontendUrl) return;
    const params = new URLSearchParams({
      user_id: auth.user.id,
      account_id: auth.user.account_id,
      target: "/users",
      referrer_url: window.location.href,
      mode: "light",
      application: "ess",
    });
    openAuthHandoff(`${adminFrontendUrl}/auth?${params.toString()}`, () => {
      const current = loadAuth();
      return current?.user.session_jwt ? {
        userId: current.user.id, accountId: current.user.account_id,
        sessionJWT: current.user.session_jwt,
      } : null;
    });
    handleClose();
  };

  const currentUserId = auth?.user.id;
  const employeeId =
    profile?.user_info?.employee_id ??
    auth?.user.user_info?.employee_id ??
    null;
  // Prefer the live profile's role over the value cached in localStorage
  // at login time — admins can change roles after the fact and the
  // sidebar avatar caption should reflect that without forcing a re-login.
  // Falls back to the first entry of `profile.roles` (lowercase keys
  // like "hr") if the display label `profile.role` ("HR") isn't set,
  // then finally to the stored `userRole`.
  const displayRole =
    profile?.role || profile?.roles?.[0] || userRole;

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        data-tour="account-menu"
        sx={{
          width: "100%",
          p: 1,
          borderRadius: "12px",
          overflow: "hidden",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Avatar
            size="medium"
            gradient
            sx={{ borderRadius: "8px", filter: "grayscale(1)" }}
          >
            {initialsOf(userName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {userName || auth?.user.email || "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {isImpersonating
                ? `impersonating · ${displayRole || "user"}`
                : displayRole || "user"}
            </Typography>
          </Box>
          <Box
            component="span"
            sx={{ color: "text.secondary", display: "inline-flex" }}
          >
            <RavenIcon icon={ChevronDown} sx={{ fontSize: 18 }} />
          </Box>
        </Stack>
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        marginThreshold={8}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              p: 0,
              mt: -1,
              overflow: "hidden",
              maxHeight: "calc(100vh - 32px)",
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        {/* Identity header */}
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              size="medium"
              gradient
              sx={{ borderRadius: "8px", filter: "grayscale(1)" }}
            >
              {initialsOf(userName)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}
                noWrap
              >
                {userName || "—"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                // `caption` renders a span; ellipsis needs a block box.
                component="p"
                sx={{ fontSize: 12.5, lineHeight: 1.4 }}
                noWrap
                title={`${auth?.user.email ?? ""}${employeeId ? ` · ${employeeId}` : ""}`}
              >
                {auth?.user.email}
                {employeeId ? ` · ${employeeId}` : ""}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Account actions */}
        <Box sx={{ px: 1, py: 0.75 }}>
          <NavRow
            icon={<RavenIcon icon={User} />}
            label="My Profile"
            onClick={() => {
              handleClose();
              navigate("/profile");
            }}
          />
          <NavRow
            icon={<RavenIcon icon={Lock} />}
            label="Change Password"
            onClick={() => {
              handleClose();
              navigate("/change-password");
            }}
          />
          {showManageUsers && (
            <NavRow
              icon={<RavenIcon icon={Users} />}
              label="Manage Users"
              onClick={handleManageUsers}
            />
          )}
          <NavRow
            icon={<RavenIcon icon={Workflow} />}
            label="View Workflow"
            onClick={() => {
              handleClose();
              navigate("/view-workflow");
            }}
          />
          <NavRow
            icon={<RavenIcon icon={Compass} />}
            label="Product Tour"
            onClick={handleStartTour}
          />
          {/* Appearance opens the design system's own switcher (Light / Dark /
              Match system, Source Sans 3 / System). The account menu stays
              open behind it so the user lands back here after choosing. */}
          <Box ref={appearanceRowRef}>
            <NavRow
              icon={<RavenIcon icon={appearance.icon} />}
              label="Appearance"
              data-tour="appearance-menu"
              onClick={() => setAppearanceOpen(true)}
              trailing={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {appearance.caption}
                  </Typography>
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", display: "inline-flex" }}
                  >
                    <RavenIcon icon={ChevronRight} sx={{ fontSize: 18 }} />
                  </Box>
                </Stack>
              }
            />
          </Box>
        </Box>

        {/* Impersonation banner */}
        {isImpersonating && originalAuth && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.24),
              })}
            >
              <Box
                component="span"
                sx={{ color: "primary.main", display: "inline-flex", mt: "1px" }}
              >
                <RavenIcon icon={Eye} sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    display: "block",
                    fontSize: 12.5,
                    lineHeight: 1.45,
                  }}
                >
                  Impersonating
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 12, lineHeight: 1.45 }}
                  noWrap
                >
                  Original: {originalAuth.user.name}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                startIcon={<RavenIcon icon={Undo2} />}
                onClick={handleReset}
                disabled={pendingId !== null}
                sx={{
                  flexShrink: 0,
                  color: "text.primary",
                  fontSize: 11.5,
                  letterSpacing: "0.08em",
                  px: 1,
                  py: 0.5,
                  minWidth: "auto",
                }}
              >
                Switch back
              </Button>
            </Box>
          </Box>
        )}

        {/* Quick switch */}
        {presetUsers.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.1em",
                  color: "text.secondary",
                  fontWeight: 600,
                  display: "block",
                  mb: 0.75,
                  lineHeight: 1.5,
                }}
              >
                User switch
              </Typography>
              <Autocomplete<PresetUser, false, false, false>
                size="small"
                fullWidth
                blurOnSelect
                options={presetUsers}
                value={null}
                disabled={pendingId !== null}
                getOptionLabel={(u) => u.name}
                getOptionDisabled={(u) => u.id === currentUserId}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_e, u) => {
                  if (u) doSwitch(u.id);
                }}
                renderOption={(props, u) => {
                  const { key, ...optionProps } =
                    props as typeof props & { key: string };
                  const isCurrent = u.id === currentUserId;
                  const isPending = pendingId === u.id;
                  return (
                    <li key={key} {...optionProps}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ width: "100%", minWidth: 0 }}
                      >
                        <Avatar size="small">
                          {initialsOf(u.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: 13.5,
                              lineHeight: 1.35,
                              color: "text.primary",
                            }}
                            noWrap
                          >
                            {u.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 12, lineHeight: 1.4 }}
                            noWrap
                          >
                            {u.label}
                          </Typography>
                        </Box>
                        {isCurrent ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ flexShrink: 0 }}
                          >
                            <Box
                              component="span"
                              sx={{ color: "primary.main", display: "inline-flex" }}
                            >
                              <RavenIcon
                                icon={CheckCircle}
                                sx={{ fontSize: 16 }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "text.secondary",
                              }}
                            >
                              Current
                            </Typography>
                          </Stack>
                        ) : isPending ? (
                          <CircularProgress
                            size={14}
                            sx={{ flexShrink: 0 }}
                          />
                        ) : null}
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select user to switch"
                  />
                )}
                ListboxProps={{ sx: { maxHeight: 280 } }}
                slotProps={{
                  paper: { sx: { borderRadius: "12px" } },
                }}
              />
            </Box>
          </>
        )}

        <Divider />

        {/* Manual ID switch */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            variant="overline"
            sx={{
              fontSize: 11,
              letterSpacing: "0.1em",
              color: "text.secondary",
              fontWeight: 600,
              display: "block",
              mb: 0.75,
              lineHeight: 1.5,
            }}
          >
            Switch by user ID
          </Typography>
          <Stack direction="row" spacing={1} alignItems="stretch">
            <TextField
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="uuid or stytch user id"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={() => doSwitch(manualId)}
              disabled={!manualId.trim() || pendingId !== null}
              startIcon={
                pendingId === manualId.trim() && pendingId ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <RavenIcon icon={ArrowLeftRight} sx={{ fontSize: 16 }} />
                )
              }
              sx={{ flexShrink: 0, minWidth: 108 }}
            >
              Switch
            </Button>
          </Stack>
          {error && (
            <Box
              sx={(theme) => ({
                mt: 1,
                px: 1.25,
                py: 1,
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.error.main, 0.06),
                border: "1px solid",
                borderColor: alpha(theme.palette.error.main, 0.2),
              })}
            >
              <Box
                component="span"
                sx={{ color: "error.main", display: "inline-flex", mt: "1px" }}
              >
                <RavenIcon icon={CircleAlert} sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 12,
                  color: "error.main",
                  lineHeight: 1.4,
                  flex: 1,
                }}
              >
                {error}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Sign out */}
        <Box sx={{ px: 1, py: 0.75 }}>
          <NavRow
            icon={<RavenIcon icon={LogOut} />}
            label="Sign Out"
            onClick={handleLogout}
            danger
          />
        </Box>
      </Menu>

      <RavenAppearanceMenu
        anchorEl={appearanceRowRef.current}
        open={appearanceOpen}
        onClose={() => setAppearanceOpen(false)}
      />
    </>
  );
}
